import { useEffect } from "react";
import { useParams } from "react-router-dom";
import bannerImg from "../../image/health/woman-interacting-with-dna.jpg";
import contentData from "../../data/content/health/healthContent.json";
import { DataStructure } from "../../types";
import { NavTree } from "../navigation/NavLinkPages";
import { BannerSection } from "../display/Sections";
import NoContent from "../content/NoContent";
import { DisplayInfo2Cols } from "../../functions/DisplayInfoFunctions";
import { useDocumentTitle } from "../../functions/useDocumentTitle";
import { useLang } from "../Context";
import { HealthEcosystemMapSection } from "../health/HealthEcosystemMapSection";
import { HealthFaqSection } from "../health/HealthFaqSection";
import { HealthHero } from "../health/HealthHero";
import { HealthLifecycleSection } from "../health/HealthLifecycleSection";
import { HealthProductSuiteSection } from "../health/HealthProductSuiteSection";
import { HealthPrimaryCta } from "../health/HealthPrimaryCta";
import { HealthProblemSection } from "../health/HealthProblemSection";
import { HealthSecondarySolutions } from "../health/HealthSecondarySolutions";
import { HealthSolutionSection } from "../health/HealthSolutionSection";
import { HealthUnitIntro } from "../health/HealthUnitIntro";
import {
    assertHealthLandingData,
    getLocalized,
    HealthLang,
} from "../health/healthTypes";
import { AimmaDoctorPage } from "../health/aimmaDoctor/AimmaDoctorPage";
import { AimmaNursePage } from "../health/aimmaNurse/AimmaNursePage";
import { AimmaPatientPage } from "../health/aimmaPatient/AimmaPatientPage";

const typedContent: DataStructure = contentData;
const imagePath = "image/health/";

interface DefaultBannerProps {
    title: string;
    subtitle: string;
}

export function DefaultBanner({ title, subtitle }: DefaultBannerProps) {
    return (
        <BannerSection
            image={bannerImg}
            title={title}
            subtitle={subtitle}
        />
    );
}

export default function Health() {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const data = typedContent["Health"];

    useDocumentTitle(getLocalized(data.pageTitle, hLang));

    useEffect(() => {
        assertHealthLandingData(data);
    }, [data]);

    return (
        <main className="overflow-x-clip text-left bg-white">
            <HealthHero data={data.hero} />
            <NavTree />
            {data.ctaSecondary && (
                <HealthSecondarySolutions data={data.ctaSecondary} />
            )}
            <HealthUnitIntro data={data.unitIntro} />
            <HealthProblemSection data={data.problem} />
            <HealthSolutionSection data={data.solution} />
            <div className="w-full overflow-x-clip bg-white">
                <HealthLifecycleSection data={data.lifecycle} />
            </div>
            <HealthProductSuiteSection data={data.productSuite} />
            <HealthEcosystemMapSection data={data.ecosystemMap} />
            <HealthFaqSection data={data.faq} />
            <HealthPrimaryCta data={data.ctaPrimary} />
        </main>
    );
}

export function HealthSubpages() {
    const { lang } = useLang();
    const { healthSolutions } = useParams();

    const isAimmaDoctor = healthSolutions === "aimma-doctor";
    const isAimmaNurse = healthSolutions === "aimma-nurse";
    const isAimmaPatient = healthSolutions === "aimma-patient";

    const data =
        !isAimmaDoctor && !isAimmaNurse && !isAimmaPatient && healthSolutions
            ? (typedContent[healthSolutions] as DataStructure | undefined)
            : undefined;
    const contentData = data?.["content"] as DataStructure | undefined;

    const subpageTitle = data?.title?.[lang]
        ? `${data.title[lang]} | Graphen Health`
        : undefined;
    useDocumentTitle(subpageTitle);

    if (isAimmaDoctor) {
        return <AimmaDoctorPage />;
    }

    if (isAimmaNurse) {
        return <AimmaNursePage />;
    }

    if (isAimmaPatient) {
        return <AimmaPatientPage />;
    }

    if (!data) {
        return <NoContent />;
    }

    return (
        <>
            <DefaultBanner
                title={data["title"][lang]}
                subtitle={data["subtitle"][lang]}
            />
            <NavTree />
            {contentData && (
                <DisplayInfo2Cols
                    contentData={contentData}
                    imagePath={imagePath}
                />
            )}
        </>
    );
}
